import React, { useEffect, useMemo, useState } from "react";
import { sizeMapping } from "../utils/mappings";
import { Size } from "../utils/types";

interface VendorLogoProps {
  vendorLogoUrl?: string;
  size?: Size;
  vendorName?: string;
}

const VendorLogo = ({ vendorLogoUrl, size = "m", vendorName }: VendorLogoProps) => {
  // The logo could be an absolute or relative path. If it's relative, it is likely we pulled it from the vendor record,
  // which will be missing the `/images` prefix.
  const logoUrl = useMemo(() => {
    if (!vendorLogoUrl) return undefined;
    if (vendorLogoUrl.trim() === "") return undefined;
    if (vendorLogoUrl.startsWith("http://") || vendorLogoUrl.startsWith("https://"))
      return vendorLogoUrl;
    return vendorLogoUrl.startsWith("/images") ? vendorLogoUrl : `/images/${vendorLogoUrl}`;
  }, [vendorLogoUrl]);

  const [imgError, setImgError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(logoUrl);

  useEffect(() => {
    setImgError(false); // Because the image tag is conditionally rendered, reset the error state when the URL changes, since the on-change handlers won't always be called.
    setCurrentUrl(logoUrl);
  }, [logoUrl]);

  /**
   * Generate a placeholder color for the background of any vendors that are missing. It's random but stable for a given
   * vendor name.
   * @param name The name of the vendor for which to generate a background color.
   */
  const generateBackgroundColor = (name?: string) => {
    if (!name) {
      return "#666666"; // Fallback color if name is empty
    }

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = `hsl(${hash % 360}, 40%, 65%)`; // Ensures a dark enough color for white text contrast.
    return color;
  };

  const handleError = () => {
    setImgError(true);
  };

  const handleLoad = () => {
    setImgError(false);
  };

  const showPlaceholder = imgError || !logoUrl;
  const imageClass = `${sizeMapping[size]} ${showPlaceholder ? "rounded-lg" : ""} inline-block`;

  return (
    <div
      className={imageClass}
      style={{
        backgroundColor: showPlaceholder ? generateBackgroundColor(vendorName) : "transparent",
      }}
    >
      {showPlaceholder ? (
        <span className={`flex items-center justify-center h-full w-full text-white text-${size}`}>
          {vendorName ? vendorName.charAt(0).toUpperCase() : "?"}
        </span>
      ) : (
        <img
          className="h-full w-full object-contain"
          src={currentUrl}
          alt={vendorName}
          onError={handleError}
          onLoad={handleLoad}
        />
      )}
    </div>
  );
};

export default VendorLogo;
